import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

/**
 * Get authentication token from localStorage
 * @returns {string|null} The access token
 */
const getAuthToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * Create axios instance with auth headers
 * @returns {Object} Axios instance with headers
 */
const createAuthHeaders = () => {
  const token = getAuthToken();
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const FeatureGateService = {
  // ===== Individual User Gates =====

  /**
   * Update feature gate for a specific user
   * @param {string} featureCode - The feature code
   * @param {number} enabled - 1 = bucket in, 0 = bucket out
   * @param {string} iboNumber - The IBO number
   * @returns {Promise} API response
   */
  updateFeatureGate: async (featureCode, enabled, iboNumber) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/um/v1/features/${featureCode}/${enabled}?iboNumber=${iboNumber}`,
        null,
        createAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Update feature gate error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get all users with a specific feature enabled
   * @param {string} featureCode - The feature code
   * @returns {Promise} List of IBO numbers
   */
  getUsersWithFeature: async (featureCode) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/um/v1/features/admin/users/${featureCode}`,
        createAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Get users with feature error:', error);
      throw error.response?.data || error;
    }
  },

  // ===== Global Feature Config =====

  /**
   * Get all feature configurations
   * @returns {Promise} List of feature configs
   */
  getAllFeatureConfigs: async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/um/v1/features/admin/configs`,
        createAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Get all feature configs error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Toggle global feature (100% rollout)
   * @param {string} featureCode - The feature code
   * @param {number} enabled - 1 = enable globally, 0 = disable globally
   * @returns {Promise} API response
   */
  toggleGlobalFeature: async (featureCode, enabled) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/um/v1/features/admin/global/${featureCode}/${enabled}`,
        null,
        createAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Toggle global feature error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get specific feature configuration
   * @param {string} featureCode - The feature code
   * @returns {Promise} Feature config
   */
  getFeatureConfig: async (featureCode) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/um/v1/features/admin/config/${featureCode}`,
        createAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Get feature config error:', error);
      throw error.response?.data || error;
    }
  },
};

export default FeatureGateService;
